import { colorFields, eyebrowField } from "./_shared";
/**
 * Testimonials — seller-curated customer quotes (quote + author + optional
 * avatar + star rating), shown in one of five layouts. Repeatable
 * `testimonial` blocks; seller-addable.
 */
export const testimonialsSchema = {
    type: "testimonials",
    name: { ar: "آراء العملاء", en: "Testimonials" },
    description: {
        ar: "اقتباسات من زبائنك مع أسمائهم وتقييمهم.",
        en: "Customer quotes with names and ratings.",
    },
    maxPerTemplate: 3,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: { ar: "طريقة عرض الآراء.", en: "How the quotes are laid out." },
            options: [
                { value: "test-carousel", label: { ar: "شرائح", en: "Carousel" } },
                { value: "test-grid", label: { ar: "شبكة", en: "Grid" } },
                { value: "test-spotlight", label: { ar: "مميز", en: "Spotlight" } },
                { value: "test-stack", label: { ar: "عمودي", en: "Stacked" } },
                { value: "test-minimal", label: { ar: "بسيط", en: "Minimal" } },
                // Ported from EasyOrders `ps-featured-press` (nature family,
                // 2026-07-20) — the inventory's "press logos" MISSING row. Same
                // content model as every other testimonial layout: a quote plus an
                // image. The image is read as a PRESS LOGO (contained, not cropped to
                // a round avatar) and the author/role lines are not rendered, because
                // the publication IS the attribution.
                { value: "test-press", label: { ar: "شعارات الصحافة", en: "Press" } },
                /**
                 * Ported from EasyOrders `sr-reviews` (dark family, 2026-07-20) — a
                 * carousel of REVIEW SCREENSHOTS: portrait 9:16 images of the reviews
                 * as they appeared in WhatsApp/Instagram, with no typed quote at all.
                 *
                 * A variant of `testimonials` and not a new type, because the section
                 * IS customer reviews — its own heading on their page is "What
                 * customers are saying" — and the block list (one image per review) is
                 * already the shape every other layout here uses. The alternative
                 * considered was `instagram` / `ig-carousel`, which has the same
                 * "title + images" mechanics but the wrong meaning: its identity is an
                 * @handle and a Follow link, and a seller looking for customer reviews
                 * would never find it there.
                 *
                 * The `quote` field is not PRINTED here (the screenshot carries the
                 * words) but it is not wasted: it becomes the image's alt text, so a
                 * screen reader gets the review. Theirs ships `alt="Customer review"`
                 * on all four, which announces nothing.
                 */
                { value: "test-screenshots", label: { ar: "صور المراجعات", en: "Screenshots" } },
                /**
                 * Ported from EasyOrders `rp-reviews` (hype family, 2026-07-20) — a
                 * card grid where each review carries a PHOTO, a verified-buyer line,
                 * stars, the review text, and a footer strip naming the product the
                 * review is about.
                 *
                 * A variant and not a new type: the block list is one review per item,
                 * exactly the shape every other layout here uses, and quote / author /
                 * role / image / rating all map straight across (`role` carries their
                 * literal "Verified Buyer" line — the role IS the attribution here).
                 *
                 * What is genuinely new is the `productId` field below. It is the only
                 * thing on their card the existing content model could not express,
                 * and dropping it would have removed a quarter of the card's height
                 * and its only link out.
                 *
                 * `image` is read as a REVIEW PHOTO — a 4:3 cover-cropped band across
                 * the top of the card — not as the 38px round avatar the other
                 * layouts use. Same argument `test-press` made for `object-fit:
                 * contain`.
                 */
                { value: "test-reviews", label: { ar: "كروت مراجعات", en: "Review cards" } },
                /**
                 * Ported from EasyOrders `hr-reviews` (prime family, 2026-07-20): a
                 * coloured band carrying one quote set at display scale, with the
                 * neighbouring quotes visible but dimmed on either side.
                 *
                 * A variant, and the check against `test-spotlight` — the other
                 * "one big quote" layout — resolved on both content and geometry:
                 *
                 *  - `test-spotlight` collapses items 2..n into avatar+name CHIPS, so
                 *    only the first quote is ever readable and the rest lose their
                 *    text entirely. This layout keeps every quote whole and simply
                 *    scrolls between them; the neighbours are dimmed to 0.22 opacity,
                 *    not summarised.
                 *  - the TYPE SCALE is the layout's identity. MEASURED, their quote is
                 *    `clamp(22px, 3.8vw, 40px)` w700 against `test-spotlight`'s
                 *    `clamp(18px, 2.6vw, 26px)` w500 — 40px vs 26px at 1440, and bold
                 *    rather than medium. The quote is the section.
                 *  - the SLIDE is `flex: 0 0 min(605px, 42vw)`, i.e. deliberately
                 *    NARROWER than the band, which is what makes the neighbours peek.
                 *    Every other testimonial layout fills its container.
                 *
                 * MEASURED on prime-books: the section is 434px at 1440 and 345px at
                 * 390, where slides go full-width and the peek becomes a 0.35-opacity
                 * edge instead of 0.22.
                 */
                { value: "test-stage", label: { ar: "اقتباس كبير", en: "Big quote" } },
            ],
            default: "test-carousel",
        },
        {
            /**
             * A supporting line under the section title. Scoped to `test-screenshots`
             * because that is the only layout here that has somewhere to put it —
             * their `sr-head` is a centred title + description pair above the
             * carousel, and the other five variants would have to invent a slot.
             *
             * `test-reviews` added 2026-07-20 (hype): their `rp-head` is the same
             * shape — a centred title with an `rp-subtitle` under it.
             */
            id: "description",
            type: "textarea",
            i18n: true,
            label: { ar: "سطر تحت العنوان", en: "Supporting line" },
            helpText: {
                ar: "سطر قصير تحت عنوان القسم.",
                en: "A short line under the section title.",
            },
            validate: { max: 160 },
            showIf: { variant: ["test-screenshots", "test-reviews"] },
        },
        ...eyebrowField(),
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان (اختياري)", en: "Title (optional)" },
            validate: { max: 60 },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        ...colorFields(),
    ],
    blocks: [
        {
            type: "testimonial",
            name: { ar: "رأي", en: "Testimonial" },
            description: { ar: "اقتباس واسم العميل.", en: "A quote and the author." },
            maxPerSection: 12,
            settings: [
                {
                    id: "quote",
                    type: "textarea",
                    i18n: true,
                    label: { ar: "الاقتباس", en: "Quote" },
                    // C2: `test-spotlight` renders `first` in full and turns `rest` into
                    // avatar + name chips (`TestimonialsSection.tsx`), so items 2..n lose
                    // their quote/rating/role. That collapse IS the layout's purpose, and a
                    // block `showIf` can't see the section's `variant` anyway — so: hint.
                    helpText: {
                        ar: "في شكل «مميز» بيظهر اقتباس الرأي الأول بس — الباقي بيبقى شارات بالاسم.",
                        en: "In the Spotlight layout only the first testimonial's quote is shown — the others become name chips.",
                    },
                    validate: { max: 280 },
                },
                {
                    id: "author",
                    type: "text",
                    i18n: true,
                    label: { ar: "الاسم", en: "Author" },
                    validate: { max: 60 },
                },
                {
                    id: "role",
                    type: "text",
                    i18n: true,
                    label: { ar: "الصفة (اختياري)", en: "Role (optional)" },
                    // C2: shown by every layout (Minimal appends it after the name), except
                    // Spotlight's name chips for items 2..n.
                    helpText: {
                        ar: "في شكل «مميز»، بتظهر للرأي الأول بس.",
                        en: "In the Spotlight layout, only the first testimonial shows it.",
                    },
                    validate: { max: 60 },
                },
                {
                    id: "image",
                    type: "image",
                    label: { ar: "الصورة الرمزية (اختياري)", en: "Avatar (optional)" },
                    // C1: `test-minimal` is a typographic list (quote + "— Name, Role") and
                    // never calls `avatar()`. Every other layout shows it, including
                    // Spotlight's chips.
                    helpText: {
                        ar: "بتظهر في كل الأشكال ما عدا «بسيط» — البسيط اقتباس والاسم بس.",
                        en: "Shown in every layout except Minimal, which is quote + name only.",
                    },
                },
                {
                    id: "rating",
                    type: "range",
                    label: { ar: "التقييم (نجوم)", en: "Rating (stars)" },
                    // C1 + C2: `test-minimal` never calls `Stars`; `test-spotlight` calls it
                    // for `first` only.
                    helpText: {
                        ar: "النجوم مش بتظهر في شكل «بسيط». وفي «مميز» بتظهر للرأي الأول بس.",
                        en: "Stars are hidden in the Minimal layout. In Spotlight, only the first testimonial shows them.",
                    },
                    min: 0,
                    max: 5,
                    step: 1,
                    default: 5,
                },
                {
                    /**
                     * The product this review is about. Added 2026-07-20 for
                     * `test-reviews` (hype `rp-reviews`), the only layout that renders
                     * it — a footer strip on the card with a 48px thumbnail, the
                     * product name clamped to two lines, and a round link through to
                     * the product page.
                     *
                     * A `productPicker` capped at ONE, reusing the machinery
                     * `shop-the-look` already uses, rather than a free-text field: the
                     * thumbnail, the name and the link all come from the catalogue, so
                     * anything the seller typed would go stale the moment the product
                     * was renamed.
                     *
                     * Unresolvable ⇒ the footer strip is simply not drawn and the card
                     * keeps its other four parts. Same degrade-never-drop rule
                     * `shop-the-look` follows for its own picks.
                     */
                    id: "productId",
                    type: "productPicker",
                    maxItems: 1,
                    label: { ar: "المنتج (اختياري)", en: "Product (optional)" },
                    helpText: {
                        ar: "المنتج اللي المراجعة دي عنه. بيظهر في شكل «كروت مراجعات» بس.",
                        en: "The product this review is about. Only the Review cards layout shows it.",
                    },
                },
            ],
        },
    ],
};
//# sourceMappingURL=testimonials.js.map